/* eslint-disable no-param-reassign */
import Vue from 'vue'
import {
  ref, reactive, computed,
} from '@vue/composition-api'
import { parseISO, format } from 'date-fns'
import AggregationApi from '@/api/v2/eod-closing/Aggregation'
import AggregationUserApi from '@/api/v2/eod-closing/AggregationUser'
import BusinessRuleApi from '@/api/v2/eod-closing/BusinessRule'
import useAggregation from '@/views/v2-temp/eod-closing/composables/useAggregation'

/**
 * 新クロージングページ（日報β専用）の日計集計ロジック。
 *
 * 既存 `eod-closing/components/Aggregations.vue` のAPI呼び出し
 * （`getAggregations` / `enqueueAggregation` / `enqueueGenerateBusinessRuleResults`
 *  / `getAggregationJobStatus` ポーリング）をそのまま流用する。
 * 既存 `eod-closing` 配下は変更せず、ロジックのみをこの composable に再構成している。
 *
 * @param {import('@vue/composition-api').Ref<string>} dateRef - 営業日 (YYYY-MM-DD)
 * @param {Object} deps - 依存オブジェクト
 * @param {Function} deps.handleApiError - APIエラーハンドラ
 */
export default function useEodClosingV2Aggregations(dateRef, { handleApiError }) {
  const isLoading = ref(true)
  const aggregations = ref([])
  const aggregationsData = ref([])
  const loadingStatuses = reactive({})
  const jobTimestamps = reactive({})
  const jobErrorMessages = reactive({})
  const jobStatuses = ref({})
  // 集計表ID -> 適用候補ユーザー一覧（適用ユーザーの変更ダイアログ用）
  const availableUsers = ref({})

  const {
    aggregationResultsMap,
    updateAggregationResult,
    sortAndFilterAggregationType,
    buildAggregationMap,
  } = useAggregation(aggregations)

  // 集計表タイプごとのデータ（position 順）
  const userAggregationsData = computed(() => sortAndFilterAggregationType(aggregationsData.value, 'EodClosingUserAggregation'))
  const tableAggregationsData = computed(() => sortAndFilterAggregationType(aggregationsData.value, 'EodClosingTableAggregation'))
  const clubAggregationsData = computed(() => sortAndFilterAggregationType(aggregationsData.value, 'EodClosingClubAggregation'))
  const userAggregationsMap = computed(() => buildAggregationMap(sortAndFilterAggregationType(aggregations.value, 'EodClosingUserAggregation')))
  const tableAggregationsMap = computed(() => buildAggregationMap(sortAndFilterAggregationType(aggregations.value, 'EodClosingTableAggregation')))
  const clubAggregationsMap = computed(() => buildAggregationMap(sortAndFilterAggregationType(aggregations.value, 'EodClosingClubAggregation')))

  const aggregationUsers = id => {
    const agg = aggregationsData.value.find(ag => ag.attributes.id === Number(id))
    if (agg?.attributes?.type === 'EodClosingUserAggregation') {
      return agg.attributes.aggregationUsers.data
    }

    return []
  }

  const showFormattedTimestamp = timestampStr => {
    if (!timestampStr) return ''

    return format(parseISO(timestampStr), 'yyyy/MM/dd HH:mm')
  }

  /**
   * 集計表の「シート状態」を判定する。
   *  - error    : ジョブ失敗
   *  - progress : 集計中
   *  - unsaved  : 集計済みだが未保存の結果がある
   *  - saved    : すべて保存済み（または対象データなし）
   */
  const sheetStatus = id => {
    const status = jobStatuses.value[id]
    if (status === 'failed' || status === 'lambda_failed') return 'error'
    if (status === 'in_progress') return 'progress'

    // 未保存判定: 結果に businessRuleResultId が無い（= isDirty / 未保存）セルがあるか
    const agg = aggregations.value.find(ag => ag?.attributes?.id === Number(id))
    if (agg?.attributes?.contexts?.data) {
      const hasUnsaved = agg.attributes.contexts.data.some(context => (
        (context.attributes?.results?.data || []).some(result => {
          const attrs = result.attributes || {}

          return attrs.value !== null
            && attrs.value !== undefined
            && !attrs.businessRuleResultId
        })
      ))
      if (hasUnsaved) return 'unsaved'
    }

    return 'saved'
  }

  const upsertAggregation = data => {
    if (!data || !data.attributes) return

    const idx = aggregations.value.findIndex(ag => ag.attributes.id === data.attributes.id)
    if (idx >= 0) {
      aggregations.value.splice(idx, 1, data)
    } else {
      aggregations.value.push(data)
    }
  }

  const getAggregationJobStatus = async id => {
    try {
      const res = await AggregationApi.getAggregationJobStatus(id, dateRef.value)
      if (res.data.status === 'success') {
        const {
          status, result, meta, errorMessage,
        } = res.data.data
        jobStatuses.value = { ...jobStatuses.value, [id]: status }
        jobErrorMessages[id] = errorMessage
        jobTimestamps[id] = { createdAt: meta.createdAt, updatedAt: meta.updatedAt }
        if (result && result.data) upsertAggregation(result.data)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        jobStatuses.value = { ...jobStatuses.value, [id]: null }
      } else {
        handleApiError(error)
      }
    }
  }

  const pollAggregationJobStatus = id => {
    let intervalTime = 5000
    const interval = setInterval(async () => {
      try {
        await getAggregationJobStatus(id)
        if (['completed', 'failed', null].includes(jobStatuses.value[id])) {
          clearInterval(interval)
        }
      } catch (error) {
        clearInterval(interval)
        if (error.response?.status !== 404) {
          handleApiError(error)
        }
      }
      intervalTime = Math.min(intervalTime * 2, 60000)
    }, intervalTime)
  }

  const enqueueAggregation = async (id, options = {}) => {
    const { forceReload = false, forceRefresh = false } = options
    try {
      const res = await AggregationApi.enqueueAggregation(id, dateRef.value, { forceReload, forceRefresh })
      if (res.status === 202) {
        jobStatuses.value = { ...jobStatuses.value, [id]: 'in_progress' }
        pollAggregationJobStatus(id)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        jobStatuses.value = { ...jobStatuses.value, [id]: null }
      } else {
        handleApiError(error)
      }
    }
  }

  const getAggregations = async () => {
    isLoading.value = true
    try {
      const res = await AggregationApi.getAggregations(dateRef.value)
      if (res.data.status === 'success') {
        const { data } = res.data.data
        aggregationsData.value = data
        const aggregationIds = []
        data.forEach(ag => {
          Vue.set(loadingStatuses, ag.attributes.id, false)
          Vue.set(jobTimestamps, ag.attributes.id, { createdAt: null, updatedAt: null })
          Vue.set(jobErrorMessages, ag.attributes.id, null)
          jobStatuses.value = { ...jobStatuses.value, [ag.attributes.id]: null }
          availableUsers.value = { ...availableUsers.value, [ag.attributes.id]: [] }
          aggregationIds.push(ag.attributes.id)
        })
        await Promise.all(aggregationIds.map(id => getAggregationJobStatus(id)))
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      isLoading.value = false
    }
  }

  // 保存結果以外を再計算（再集計）
  const reloadAggregation = id => {
    enqueueAggregation(id, { forceReload: true })
  }

  // 保存結果を削除してすべてを再計算（リセットして再集計）
  const refreshAggregation = id => {
    enqueueAggregation(id, { forceReload: true, forceRefresh: true })
  }

  // カテゴリ単位の「日計ジョブ一括実行」。
  // 既存 `eod-closing/components/Aggregations.vue` の enqueueAggregations を参考に、
  // 指定タイプの集計表のうち未実行（null）または失敗（failed）のものをまとめて
  // enqueue する。集計中（in_progress）/完了済み（completed）はスキップ。
  const enqueueAggregationsByType = async type => {
    const data = {
      EodClosingUserAggregation: userAggregationsData.value,
      EodClosingTableAggregation: tableAggregationsData.value,
      EodClosingClubAggregation: clubAggregationsData.value,
    }[type]
    if (!data) return

    const promises = data
      .filter(agg => {
        const status = jobStatuses.value[agg.attributes.id]

        return status === null || status === undefined || status === 'failed'
      })
      .map(agg => enqueueAggregation(agg.attributes.id))
    await Promise.all(promises)
  }

  // 一括保存
  const enqueueGenerateBusinessRuleResults = async (id, options = {}) => {
    const { forceReload = false, forceRefresh = false } = options
    try {
      const res = await BusinessRuleApi.enqueueGenerateBusinessRuleResults(id, dateRef.value, { forceReload, forceRefresh })
      if (res.status === 202) {
        jobStatuses.value = { ...jobStatuses.value, [id]: 'in_progress' }
        pollAggregationJobStatus(id)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        jobStatuses.value = { ...jobStatuses.value, [id]: null }
      } else {
        handleApiError(error)
      }
    }
  }

  // 保存レスポンスの context と、集計データ側の context が同一かを判定する。
  // Club コンテキストは集計表に 1 つだけのため contextType 一致で同一とみなす
  // （Club の contextId は集計データ側とレスポンス側で持ち方が異なりうる）。
  // User / Table は contextId を null 許容で突き合わせる。
  const isSameContext = (ctxAttrs, contextType, contextId) => {
    if (ctxAttrs.contextType !== contextType) return false
    if (contextType === 'Club') return true

    return (ctxAttrs.id ?? null) === (contextId ?? null)
  }

  /**
   * 保存APIのレスポンス（BusinessRuleResult）を集計データへマージする。
   *
   * 既存 `updateAggregationResult` は「既に result 行があるセル」しか更新できず、
   * 空セル（result 行が存在しないセル）への初入力は反映できなかった。
   * ここでは、対象 context の results.data に該当行が無ければ
   * 取得時と同じ形（AggregationFetch::ResultSerializer 相当）の行を新規追加する。
   *
   * 保存レスポンスの attributes:
   *   { id, businessRuleId, resultValue, contextId, contextType, ... }
   * 取得時の result.attributes:
   *   { id, businessRuleId, businessRuleResultId, value, editable, ... }
   *
   * @param {Object} businessRuleResult - 保存APIが返した BusinessRuleResult
   */
  const mergeBusinessRuleResult = businessRuleResult => {
    const attrs = businessRuleResult?.attributes
    if (!attrs) return

    const {
      id: businessRuleResultId,
      businessRuleId,
      resultValue,
      contextType,
      contextId,
    } = attrs

    // まず既存行の更新を試みる（依存ルールの再計算結果なども含めて反映）。
    updateAggregationResult(businessRuleResult)

    // 既存行が無い場合に備え、対象 context の results.data へ新規行を追加する。
    aggregations.value.forEach(aggregation => {
      const contexts = aggregation?.attributes?.contexts?.data
      if (!Array.isArray(contexts)) return

      contexts.forEach(context => {
        const ctxAttrs = context?.attributes
        if (!ctxAttrs) return
        if (!isSameContext(ctxAttrs, contextType, contextId)) return

        if (!ctxAttrs.results) Vue.set(ctxAttrs, 'results', { data: [] })
        if (!Array.isArray(ctxAttrs.results.data)) Vue.set(ctxAttrs.results, 'data', [])

        const resultsData = ctxAttrs.results.data
        const existing = resultsData.find(r => r?.attributes?.businessRuleId === businessRuleId)

        if (existing) {
          // 既存行は updateAggregationResult で更新済み。
          // 念のため value / businessRuleResultId を確定値で揃える。
          Vue.set(existing.attributes, 'value', resultValue)
          Vue.set(existing.attributes, 'businessRuleResultId', businessRuleResultId)

          return
        }

        // 新規行を取得時と同じ形で追加する。
        resultsData.push({
          id: `new-${businessRuleResultId}`,
          type: 'result',
          attributes: {
            id: `new-${businessRuleResultId}`,
            businessRuleId,
            businessRuleResultId,
            value: resultValue,
            dependentResources: [],
            dependentBusinessRuleIds: [],
            editable: true,
          },
        })
      })
    })
  }

  // セル単位保存
  const generateBusinessRuleResult = async ({
    aggregationId,
    contextType,
    contextId,
    businessRuleId,
    resultValue,
  }) => {
    if (contextType !== 'Club' && !contextId) throw new TypeError('You need to specify contextId')

    Object.keys(loadingStatuses).forEach(id => { loadingStatuses[id] = true })
    try {
      const res = await BusinessRuleApi.generateBusinessRuleResult({
        aggregationId,
        contextType,
        contextId,
        businessRuleId,
        resultValue,
        date: dateRef.value,
      })
      if (res.data.status === 'success') {
        // 既存行の更新だけでなく、空セル（result 行なし）への初入力も
        // 新規行追加で反映する。
        res.data.data.data.forEach(businessRuleResult => mergeBusinessRuleResult(businessRuleResult))
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      Object.keys(loadingStatuses).forEach(id => { loadingStatuses[id] = false })
    }
  }

  // --- 適用ユーザーの変更 ----------------------------------------------
  // 既存 `eod-closing/components/Aggregations.vue` の
  // createEodClosingUserAggregation / destroyEodClosingUserAggregation /
  // handleFetchAvailableUsers を参考に、新クロージングページ側に再構成したもの。

  // 適用候補ユーザー一覧を取得する
  const fetchAvailableUsers = async (id, callback = () => {}) => {
    try {
      const res = await AggregationApi.getAvailableUsers(id)
      if (res.data.status === 'success') {
        const { data } = res.data.data
        availableUsers.value = { ...availableUsers.value, [id]: data }
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      callback()
    }
  }

  // 集計表に適用ユーザーを追加する
  const createUserAggregation = async (
    id,
    { userId, callback } = { userId: null, callback: () => {} },
  ) => {
    try {
      const res = await AggregationUserApi.createAggregationUser(id, userId)
      if (res.data.status === 'success') {
        const { data } = res.data.data
        const aggIdx = aggregationsData.value.findIndex(ag => ag.attributes.id === Number(id))
        if (aggIdx < 0) return

        const updatedAggData = { ...aggregationsData.value[aggIdx] }
        updatedAggData.attributes.aggregationUsers.data.push(data)
        aggregationsData.value.splice(aggIdx, 1, updatedAggData)

        const updatedAvailableUsers = availableUsers.value[id] ? [...availableUsers.value[id]] : []
        const userIdx = updatedAvailableUsers.findIndex(user => user.attributes.id === Number(userId))
        if (userIdx < 0) return

        updatedAvailableUsers.splice(userIdx, 1)
        availableUsers.value = { ...availableUsers.value, [id]: updatedAvailableUsers }
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      callback()
    }
  }

  // 集計表から適用ユーザーを削除する
  const destroyUserAggregation = async (
    aggregationId,
    { id, userId, callback } = { id: null, userId: null, callback: () => {} },
  ) => {
    try {
      const res = await AggregationUserApi.destroyAggregationUser(id)
      if (res.data.status === 'success') {
        const { data } = res.data.data

        const aggIdx = aggregationsData.value.findIndex(ag => ag.attributes.id === Number(aggregationId))
        if (aggIdx < 0) return

        const updatedAggData = { ...aggregationsData.value[aggIdx] }
        const aggUserIdx = updatedAggData.attributes.aggregationUsers.data.findIndex(
          aggUser => aggUser.attributes.id === Number(id),
        )
        if (aggUserIdx < 0) return

        updatedAggData.attributes.aggregationUsers.data.splice(aggUserIdx, 1)
        aggregationsData.value.splice(aggIdx, 1, updatedAggData)

        const updatedAvailableUsers = availableUsers.value[id] ? [...availableUsers.value[id]] : []
        const userIdx = updatedAvailableUsers.findIndex(user => user.attributes.id === Number(userId))
        if (userIdx < 0) return

        updatedAvailableUsers.splice(userIdx, 1, data)
        availableUsers.value = { ...availableUsers.value, [id]: updatedAvailableUsers }
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      callback()
    }
  }

  return {
    isLoading,
    aggregations,
    aggregationsData,
    loadingStatuses,
    jobTimestamps,
    jobErrorMessages,
    jobStatuses,
    availableUsers,
    aggregationResultsMap,

    userAggregationsData,
    tableAggregationsData,
    clubAggregationsData,
    userAggregationsMap,
    tableAggregationsMap,
    clubAggregationsMap,

    aggregationUsers,
    showFormattedTimestamp,
    sheetStatus,

    getAggregations,
    reloadAggregation,
    refreshAggregation,
    enqueueAggregationsByType,
    enqueueGenerateBusinessRuleResults,
    generateBusinessRuleResult,
    fetchAvailableUsers,
    createUserAggregation,
    destroyUserAggregation,
  }
}
